/**
 * React Query mutation hook for creating batch invoices
 * Handles batch invoice creation with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBatchInvoice } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to create batch invoices using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful creation
 * @param {Function} options.onError - Callback function called on creation error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state (deprecated in React Query v5, use isPending)
 * @returns {boolean} returns.isPending - Pending state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const createBatchInvoiceMutation = useCreateBatchInvoiceMutation({
 *   onSuccess: (data) => {
 *     console.log('Batch invoices created successfully', data);
 *   },
 *   onError: (error) => {
 *     console.error('Failed to create batch invoices:', error);
 *   },
 * });
 *
 * // Usage
 * createBatchInvoiceMutation.mutate({
 *   case_contact_info: {...},
 *   time_entry_start_date: '2024-01-01T00:00:00Z',
 *   time_entry_end_date: '2024-12-31T23:59:59Z',
 *   invoice_date: '2025-01-10T00:00:00Z',
 *   invoice_due_date: '2025-02-10T00:00:00Z',
 *   payment_reminder: true,
 *   pull_time_entries: true,
 *   pull_expenses: true,
 *   adjustment_type: 'percentage',
 *   adjustment_value: 10.0,
 *   deposit_account: 'Operating Account',
 *   apply_available_trust_funds: true,
 *   include_unpaid_invoice_balance_forward: true,
 *   invoice_sharing: ['main_client', 'billing_contact']
 * });
 */
export const useCreateBatchInvoiceMutation = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return createBatchInvoice(data);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: queryKeys.batchBilling.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useCreateBatchInvoiceMutation;
